import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productAPI } from '../../utils/api';
import { analyticsAPI } from '../../utils/api';
import { useCart } from '../../context/CartContext';
import { ArrowRight, Star, Leaf, Truck, RotateCcw, Zap } from 'lucide-react';

function ProductCard({ product }) {
  const { addItem } = useCart();
  const images = (() => { try { return JSON.parse(product.images); } catch { return [product.images]; } })();

  return (
    <div className="hp-product-card">
      <Link to={`/product/${product.slug}`} className="hp-product-img-wrap">
        {product.compare_price && (
          <span className="hp-product-discount">
            -{Math.round((1 - product.price / product.compare_price) * 100)}%
          </span>
        )}
        {product.is_featured && <span className="hp-product-tag">⭐ Bestseller</span>}
        <img src={images[0]} alt={product.name} />
      </Link>
      <div className="hp-product-info">
        <div className="hp-product-cat">{product.category_name}</div>
        <Link to={`/product/${product.slug}`} className="hp-product-name">{product.name}</Link>
        <div className="hp-product-meta">
          <div className="hp-stars">
            {'★'.repeat(Math.round(product.avg_rating || 5))}{'☆'.repeat(5 - Math.round(product.avg_rating || 5))}
            <span>({product.review_count || 0})</span>
          </div>
        </div>
        <div className="hp-product-bottom">
          <div className="hp-price">
            <span className="hp-price-current">${product.price}</span>
            {product.compare_price && <span className="hp-price-old">${product.compare_price}</span>}
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => addItem(product.id)}>+ Shto</button>
        </div>
      </div>
    </div>
  );
}

const TESTIMONIALS = [
  { name: 'Sarah M.', loc: 'New York, USA', text: 'Eliksiri ndryshoi plotësisht flokët e mi. Pas 3 javësh — më të trasha, me shkëlqim, zero rënie!', rating: 5 },
  { name: 'Arjeta K.', loc: 'Prishtinë, Kosovë', text: 'Brenda 20 ditëve vetullat dhe qerpikët janë shumë më të trasha. Produkt fantastik, e rekomandoj!', rating: 5 },
  { name: 'Laura R.', loc: 'Chicago, USA', text: 'Isha duke humbur flokët 2 vjet. Pas 8 javësh eliksiri — flokë të rinj kudo. Ndryshoi jetën time.', rating: 5 },
];

export default function HomePage() {
  const { data } = useQuery({ queryKey: ['featured-products'], queryFn: () => productAPI.getAll({ featured: 'true', limit: 4 }).then(r => r.data) });

  useEffect(() => { analyticsAPI.track({ event_type: 'page_view', page_path: '/' }); }, []);

  return (
    <div className="homepage">
      {/* Hero */}
      <section className="hero-section">
        <div className="hero-inner">
          <div className="hero-text">
            <div className="hero-pill">🌿 100% Natyral · Pa Kimikate</div>
            <h1>Natyra's Best<br /><em>për Flokë të Bukur</em></h1>
            <p>Formula të fuqishme natyrale — për të forcuar, rritur dhe mbrojtur flokët tuaj. Zero kimikate, rezultate të vërteta.</p>
            <div className="hero-ctas">
              <Link to="/shop" className="btn btn-gold btn-lg">Shfleto Produktet <ArrowRight size={18} /></Link>
              <Link to="/shop?featured=true" className="btn btn-outline btn-lg" style={{ color: 'white', borderColor: 'rgba(255,255,255,.4)' }}>
                Shiko Ofertat
              </Link>
            </div>
            <div className="hero-stats">
              <div><strong>4.9★</strong><span>Rating Mesatar</span></div>
              <div><strong>15K+</strong><span>Klientë</span></div>
              <div><strong>100%</strong><span>Natyral</span></div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-visual-inner">
              <div className="hero-badge-float top">⭐ Bestseller #1</div>
              <div className="hero-img-wrap">
                <img src="https://cdn11.bigcommerce.com/s-6mhc01ngp9/images/stencil/640w/products/111/419/IMG_1995__30401.1774289566.png?c=1" alt="Eliksir" />
              </div>
              <div className="hero-badge-float bottom">🌿 Pa Sulfate · Pa Parabene</div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <div className="trust-bar">
        <div className="trust-bar-inner">
          {[['🚚','Dërgim Falas +$49'],['🌿','Zero Kimikate'],['↩️','30-Ditë Kthim'],['💰','Kurseni 10% me Abonim'],['⭐','6,500+ Recensione']].map(([icon, text]) => (
            <div key={text} className="trust-item"><span>{icon}</span>{text}</div>
          ))}
        </div>
      </div>

      {/* Featured Products */}
      <section className="section">
        <div className="page-container">
          <div className="section-header-row">
            <div>
              <div className="section-eyebrow">Koleksioni Ynë</div>
              <h2 className="section-title">Produktet Bestseller</h2>
            </div>
            <Link to="/shop" className="link-all">Shiko të gjitha <ArrowRight size={16} /></Link>
          </div>
          <div className="hp-products-grid">
            {data?.products?.map(p => <ProductCard key={p.id} product={p} />)}
            {!data && Array(4).fill(0).map((_,i) => <div key={i} className="product-skeleton" />)}
          </div>
        </div>
      </section>

      {/* Offer Banner */}
      <section className="offer-banner">
        <div className="page-container">
          <div className="offer-inner">
            <div>
              <div className="section-eyebrow" style={{ color: 'var(--gold-light)' }}>Ofertë e Kufizuar</div>
              <h2 style={{ color: 'white', fontSize: '2.2rem' }}>Bli 2, Merr 1 <em style={{ color: 'var(--gold-light)' }}>Falas</em></h2>
              <p style={{ color: 'rgba(255,255,255,.7)', margin: '14px 0 28px' }}>Bli çdo 2 Eliksir dhe merr Eyebrow Oil falas. Ofertë e kufizuar!</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <Link to="/shop" className="btn btn-gold">Përfito Tani</Link>
                <Link to="/shop" className="btn btn-outline" style={{ color:'white', borderColor:'rgba(255,255,255,.4)' }}>Shiko Produktet</Link>
              </div>
            </div>
            <img src="https://cdn11.bigcommerce.com/s-6mhc01ngp9/images/stencil/640w/products/111/419/IMG_1995__30401.1774289566.png?c=1" alt="Bundle" />
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="section">
        <div className="page-container">
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div className="section-eyebrow">Pse HairFix?</div>
            <h2 className="section-title">Formula Jonë Ndryshon Gjithçka</h2>
          </div>
          <div className="why-grid">
            {[
              { icon: <Leaf size={28} />, title: '100% Natyral', desc: 'Çdo përbërës është i pastër natyral — pa sulfate, parabene, apo kimikate të dëmshme.' },
              { icon: <Zap size={28} />, title: 'Rezultate të Shpejta', desc: 'Shumica e klientëve shohin ndryshim brenda 3-6 javësh të para të përdorimit.' },
              { icon: <Star size={28} />, title: '6,500+ Recensione 5★', desc: 'Mijëra klientë të kënaqur — vlerësimet tregojnë efektivitetin e produkteve tona.' },
              { icon: <Truck size={28} />, title: 'Dërgim i Shpejtë', desc: 'Dorëzim brenda 2-5 ditëve pune. Falas për porosi mbi $49.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="why-card">
                <div className="why-icon">{icon}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section" style={{ background: 'var(--cream-dark)' }}>
        <div className="page-container">
          <div style={{ textAlign:'center', marginBottom:48 }}>
            <div className="section-eyebrow">Dëshmitë</div>
            <h2 className="section-title">Çfarë Thonë Klientët</h2>
          </div>
          <div className="testimonials-row">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="testimonial-card">
                <div className="t-stars">{'★'.repeat(t.rating)}</div>
                <p>"{t.text}"</p>
                <div className="t-author">
                  <div className="t-avatar">{t.name[0]}</div>
                  <div><strong>{t.name}</strong><br /><small>{t.loc}</small></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="final-cta">
        <div className="page-container" style={{ textAlign: 'center' }}>
          <div className="section-eyebrow" style={{ color: 'var(--gold-light)', justifyContent: 'center' }}>Fillo Sot</div>
          <h2 style={{ color: 'white', fontSize: 'clamp(2rem,4vw,3rem)', margin: '12px 0 18px' }}>Transformo Flokët Tuaj Natyralë</h2>
          <p style={{ color: 'rgba(255,255,255,.65)', marginBottom: 40, fontSize: '1rem' }}>Bashkohu me 15,000+ klientë të kënaqur dhe zbulo fuqinë e natyrës.</p>
          <Link to="/shop" className="btn btn-gold btn-lg">Shfleto Tani <ArrowRight size={18} /></Link>
        </div>
      </section>

      <style>{`
        .homepage { padding-top: 68px; }
        /* Hero */
        .hero-section { background: linear-gradient(135deg, var(--green-deep) 0%, var(--green-mid) 70%, #3A6B50 100%); min-height: calc(100vh - 68px); display:flex;align-items:center; }
        .hero-inner { max-width:1200px;margin:0 auto;padding:60px 24px;display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:center; }
        .hero-pill { display:inline-flex;align-items:center;gap:8px;background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.2);color:rgba(255,255,255,.9);padding:6px 16px;border-radius:999px;font-size:.78rem;font-weight:600;letter-spacing:.06em;text-transform:uppercase;margin-bottom:24px; }
        .hero-text h1 { font-size:clamp(2.5rem,5vw,3.8rem);color:white;margin-bottom:20px; }
        .hero-text h1 em { color:var(--gold-light); }
        .hero-text p { color:rgba(255,255,255,.72);font-size:1.05rem;margin-bottom:36px;max-width:440px; }
        .hero-ctas { display:flex;gap:14px;flex-wrap:wrap;margin-bottom:48px; }
        .hero-stats { display:flex;gap:40px;padding-top:32px;border-top:1px solid rgba(255,255,255,.15); }
        .hero-stats > div { display:flex;flex-direction:column;gap:4px; }
        .hero-stats strong { font-family:'Playfair Display',serif;font-size:1.8rem;color:var(--gold-light); }
        .hero-stats span { font-size:.72rem;text-transform:uppercase;letter-spacing:.06em;color:rgba(255,255,255,.55); }
        .hero-visual { display:flex;justify-content:center;align-items:center;position:relative; }
        .hero-visual-inner { position:relative;display:flex;flex-direction:column;align-items:center;gap:16px; }
        .hero-img-wrap { width:260px;height:320px;background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.15);border-radius:var(--radius-xl);display:flex;align-items:center;justify-content:center;backdrop-filter:blur(8px); }
        .hero-img-wrap img { width:200px;height:240px;object-fit:contain; }
        .hero-badge-float { background:var(--white);color:var(--green-deep);padding:8px 18px;border-radius:999px;font-size:.78rem;font-weight:700;box-shadow:var(--shadow-md); }
        /* Trust */
        .trust-bar { background:var(--green-deep);padding:16px 24px; }
        .trust-bar-inner { max-width:1200px;margin:0 auto;display:flex;justify-content:center;gap:48px;flex-wrap:wrap; }
        .trust-item { display:flex;align-items:center;gap:8px;color:rgba(255,255,255,.8);font-size:.82rem;font-weight:500;white-space:nowrap; }
        /* Products */
        .section-header-row { display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:40px;flex-wrap:wrap;gap:16px; }
        .link-all { display:flex;align-items:center;gap:6px;font-size:.85rem;font-weight:600;color:var(--green-mid);border-bottom:1px solid var(--green-mid);padding-bottom:2px; }
        .hp-products-grid { display:grid;grid-template-columns:repeat(4,1fr);gap:24px; }
        .hp-product-card { background:var(--white);border-radius:var(--radius-lg);overflow:hidden;transition:transform .3s,box-shadow .3s; }
        .hp-product-card:hover { transform:translateY(-6px);box-shadow:var(--shadow-lg); }
        .hp-product-img-wrap { display:block;background:var(--cream);padding:24px;min-height:200px;display:flex;align-items:center;justify-content:center;position:relative; }
        .hp-product-img-wrap img { width:130px;height:160px;object-fit:contain;transition:transform .4s; }
        .hp-product-card:hover .hp-product-img-wrap img { transform:scale(1.07); }
        .hp-product-tag { position:absolute;top:12px;left:12px;background:var(--gold);color:var(--green-deep);font-size:.65rem;font-weight:700;text-transform:uppercase;letter-spacing:.05em;padding:4px 10px;border-radius:999px; }
        .hp-product-discount { position:absolute;top:12px;right:12px;background:var(--danger);color:white;font-size:.65rem;font-weight:700;padding:4px 8px;border-radius:999px; }
        .hp-product-info { padding:18px; }
        .hp-product-cat { font-size:.7rem;text-transform:uppercase;letter-spacing:.06em;color:var(--text-light);margin-bottom:4px; }
        .hp-product-name { display:block;font-family:'Playfair Display',serif;font-size:1.05rem;font-weight:600;color:var(--green-deep);margin-bottom:8px; }
        .hp-stars { color:var(--gold);font-size:.82rem; }
        .hp-stars span { color:var(--text-light);font-size:.72rem;margin-left:4px; }
        .hp-product-bottom { display:flex;align-items:center;justify-content:space-between;margin-top:14px; }
        .hp-price { display:flex;align-items:baseline;gap:8px; }
        .hp-price-current { font-family:'Playfair Display',serif;font-size:1.15rem;font-weight:700;color:var(--green-deep); }
        .hp-price-old { font-size:.82rem;color:var(--text-light);text-decoration:line-through; }
        .product-skeleton { background:var(--cream-dark);border-radius:var(--radius-lg);min-height:340px;animation:pulse 1.5s ease infinite; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
        /* Offer */
        .offer-banner { background:var(--green-deep);padding:72px 0; }
        .offer-inner { display:grid;grid-template-columns:1fr auto;gap:48px;align-items:center; }
        .offer-inner img { width:260px;object-fit:contain;opacity:.9; }
        /* Why */
        .why-grid { display:grid;grid-template-columns:repeat(4,1fr);gap:24px; }
        .why-card { background:var(--white);border-radius:var(--radius-lg);padding:28px;text-align:center;transition:transform .3s,box-shadow .3s; }
        .why-card:hover { transform:translateY(-4px);box-shadow:var(--shadow-md); }
        .why-icon { width:56px;height:56px;border-radius:50%;background:var(--green-soft);display:flex;align-items:center;justify-content:center;margin:0 auto 16px;color:var(--green-deep); }
        .why-card h3 { font-size:1rem;margin-bottom:8px; }
        .why-card p { font-size:.85rem;color:var(--text-light);line-height:1.6; }
        /* Testimonials */
        .testimonials-row { display:grid;grid-template-columns:repeat(3,1fr);gap:24px; }
        .testimonial-card { background:var(--white);border-radius:var(--radius-lg);padding:28px; }
        .t-stars { color:var(--gold);font-size:1rem;margin-bottom:14px; }
        .testimonial-card p { font-size:.9rem;line-height:1.7;color:var(--text-mid);margin-bottom:20px; }
        .t-author { display:flex;align-items:center;gap:12px; }
        .t-avatar { width:40px;height:40px;border-radius:50%;background:var(--green-soft);display:flex;align-items:center;justify-content:center;font-family:'Playfair Display',serif;font-size:1rem;font-weight:700;color:var(--green-deep);flex-shrink:0; }
        .t-author strong { font-size:.88rem; }
        .t-author small { color:var(--text-light);font-size:.76rem; }
        /* Final CTA */
        .final-cta { background:linear-gradient(135deg,var(--green-deep) 0%,var(--green-mid) 100%);padding:120px 0; }
        /* Responsive */
        @media(max-width:900px) {
          .hero-inner { grid-template-columns:1fr; }
          .hero-visual { display:none; }
          .hp-products-grid { grid-template-columns:repeat(2,1fr); }
          .why-grid { grid-template-columns:repeat(2,1fr); }
          .testimonials-row { grid-template-columns:1fr; }
          .offer-inner { grid-template-columns:1fr; }
          .offer-inner img { width:200px;margin:0 auto; }
          .trust-bar-inner { gap:20px; }
        }
        @media(max-width:500px) {
          .hp-products-grid { grid-template-columns:1fr; }
          .why-grid { grid-template-columns:1fr; }
        }
      `}</style>
    </div>
  );
}
